import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Clock,
  FileText,
  MessageSquare,
  Megaphone,
  User,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

import { Switch } from '@/components/ui/switch'
import {
  useUnreadCount,
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  type NotificationType,
  type Notification,
} from '@/hooks'
import {
  useDoNotDisturb,
  useUpdateEfficiencyPreferences,
} from '@/hooks'
import { cn } from '@/lib/utils'

// ==================== 通知类型图标映射 ====================

const NOTIFICATION_TYPE_ICONS: Record<NotificationType, typeof Bell> = {
  approval_request: FileText,
  approval_result: Check,
  task_assigned: User,
  task_mentioned: MessageSquare,
  system_announcement: Megaphone,
}

// ==================== 相对时间格式化 ====================

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return '刚刚'
  } else if (diffMinutes < 60) {
    return `${diffMinutes} 分钟前`
  } else if (diffHours < 24) {
    return `${diffHours} 小时前`
  } else if (diffDays < 7) {
    return `${diffDays} 天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

// ==================== 通知项组件 ====================

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: string) => void
  isMarkingRead: boolean
}

function NotificationItem({
  notification,
  onMarkAsRead,
  isMarkingRead,
}: NotificationItemProps) {
  const Icon = NOTIFICATION_TYPE_ICONS[notification.type]

  return (
    <div
      className={cn(
        'flex gap-3 p-3 hover:bg-accent cursor-pointer transition-colors',
        !notification.isRead && 'bg-accent/50'
      )}
      onClick={() => {
        if (!notification.isRead && !isMarkingRead) {
          onMarkAsRead(notification.id)
        }
      }}
    >
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          notification.isRead ? 'bg-muted' : 'bg-primary/10'
        )}
      >
        <Icon className={cn('h-4 w-4', notification.isRead ? 'text-muted-foreground' : 'text-primary')} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn('text-sm font-medium truncate', !notification.isRead && 'text-foreground')}>
            {notification.title}
          </span>
          {!notification.isRead && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{notification.content}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
          <Clock className="h-3 w-3" />
          <span>{formatRelativeTime(notification.createdAt)}</span>
        </div>
      </div>
    </div>
  )
}

// ==================== 通知面板组件 ====================

interface NotificationPanelProps {
  isOpen: boolean
  onClose: () => void
}

function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const { data: notificationsData, isLoading } = useNotifications({ pageSize: 20 })
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const doNotDisturb = useDoNotDisturb()
  const updatePreferences = useUpdateEfficiencyPreferences()

  const notifications = notificationsData?.data ?? []
  const hasUnread = notifications.some((n) => !n.isRead)

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id)
  }

  const handleToggleDoNotDisturb = () => {
    updatePreferences.mutate({ doNotDisturb: !doNotDisturb })
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="absolute right-0 top-full mt-2 w-96 rounded-lg border bg-card shadow-lg z-50">
      {/* 头部 */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-medium">通知</h3>
        <div className="flex items-center gap-3">
          {/* 勿扰模式开关 */}
          <div className="flex items-center gap-2">
            <BellOff className={cn('h-4 w-4', doNotDisturb ? 'text-primary' : 'text-muted-foreground')} />
            <Switch
              checked={doNotDisturb}
              onCheckedChange={handleToggleDoNotDisturb}
              disabled={updatePreferences.isPending}
              aria-label="勿扰模式"
            />
          </div>
          {hasUnread && (
            <button
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
              className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
            >
              <CheckCheck className="h-3 w-3" />
              全部已读
            </button>
          )}
        </div>
      </div>

      {/* 勿扰模式提示 */}
      {doNotDisturb && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b text-amber-700 dark:text-amber-400">
          <BellOff className="h-4 w-4" />
          <span className="text-xs">勿扰模式已开启</span>
        </div>
      )}

      {/* 通知列表 */}
      <div className="max-h-[28rem] overflow-y-auto divide-y">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Bell className="h-8 w-8 mb-2" />
            <span className="text-sm">暂无通知</span>
          </div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              isMarkingRead={markAsRead.isPending}
            />
          ))
        )}
      </div>

      {/* 底部 */}
      <div className="border-t px-4 py-2">
        <a
          href="/notifications"
          onClick={onClose}
          className="block text-center text-sm text-primary hover:underline"
        >
          查看全部通知
        </a>
      </div>
    </div>
  )
}

// ==================== 通知铃铛组件 ====================

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { data: unreadData } = useUnreadCount()
  const doNotDisturb = useDoNotDisturb()

  const unreadCount = unreadData?.count ?? 0

  // 点击外部关闭面板
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    return undefined
  }, [isOpen])

  // 格式化未读数量显示
  const displayCount = unreadCount > 99 ? '99+' : unreadCount.toString()

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative rounded-md p-2 hover:bg-accent"
        aria-label={`通知${unreadCount > 0 ? `，${unreadCount} 条未读` : ''}${doNotDisturb ? '，勿扰模式已开启' : ''}`}
      >
        {doNotDisturb ? (
          <BellOff className="h-5 w-5 text-muted-foreground" />
        ) : (
          <Bell className="h-5 w-5" />
        )}
        {unreadCount > 0 && !doNotDisturb && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-xs font-medium text-destructive-foreground">
            {displayCount}
          </span>
        )}
      </button>

      <NotificationPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  )
}
